import axios, { AxiosError } from 'axios';
import axiosRetry, { isNetworkOrIdempotentRequestError } from 'axios-retry';

import { DataDogLogger } from 'utils/dataDog';
import { CUSTOM_ACTION_NAMES } from 'utils/dataDog/constants';
import { DELAY_BETWEEN_RETRIES, DEFAULT_RETRIES } from './constants';
import { AxiosWithRetryType } from './types';

/**
 * Create a wrapper function for axios API client that has delay interceptor attached. Reference in code as-needed.
 * Logic will repeat API call until success or retries reaches 0. Then promise will resolve w/ data or error object.
 * @returns object - data after successful HTTP request from API.
 */
const axiosWithRetry = async ({
  body,
  headers,
  method,
  url,
  customRetryCondition,
  retries = DEFAULT_RETRIES,
}: AxiosWithRetryType) => {
  let hasAttemptedRetry = false;
  let successRetryLogData = {};
  const retryErrorMessages: Array<string> = [];

  const defaultRetryCondition = (e: AxiosError) => {
    const isGatewayTimeout = e?.response?.status === 504; // there's ongoing reliability issues w/ Propel APIs
    return isGatewayTimeout || isNetworkOrIdempotentRequestError(e);
  };

  axiosRetry(axios, {
    retries,
    onRetry: (retryCount, e, requestConfig) => {
      hasAttemptedRetry = true;
      retryErrorMessages.push(e.message);
      successRetryLogData = { url, method, retryCount, messages: retryErrorMessages };
    },
    retryDelay: (retryCount) => retryCount * DELAY_BETWEEN_RETRIES,
    retryCondition: (e: AxiosError) =>
      customRetryCondition ? customRetryCondition(e) : defaultRetryCondition(e),
  });

  const options = { data: body, method, headers, url };
  const res = await axios(options);
  // Can only reach here if successful. Otherwise if unsuccessful, will fall into invocation's closest catch block.
  if (hasAttemptedRetry) {
    DataDogLogger.action(CUSTOM_ACTION_NAMES.API_RETRY_SUCCESSFUL, successRetryLogData);
  }
  return res;
};

export { axiosWithRetry };
